import type { AuthUser, UserType } from '../Service/AuthApiService'

import AuthApiService, { AuthError } from '../Service/AuthApiService'
import LoginStateService from '../Service/LoginStateService'
import { getElementChild, getElementChildren, getElements } from '../Utility/Dom'
import { IS_HIDDEN, IS_LOADING } from '../Utility/State'
import { LOGIN_MODAL_OPENED_EVENT } from './LoginModal'

const OTP_LENGTH = 6
// Mirrors OTP_EXPIRY_MINUTES in the middleware — a shorter value would count down to zero
// while the code is still accepted.
const OTP_TIMEOUT_MS = 15 * 60 * 1000
const SUCCESS_AUTO_CLOSE_MS = 3 * 1000
// Mirrors OTP_RESEND_COOLDOWN in the middleware, so a resend is blocked locally rather than
// coming back as a 429 the user cannot make sense of.
const RESEND_COOLDOWN_MS = 60 * 1000

// Reasons the middleware reports once the pending code is gone for good: a new one can be
// requested right away, so the resend lock is lifted instead of being waited out.
const CODE_GONE_CODES = new Set(['exceeded', 'expired', 'no_pending_otp'])

interface FlowRefs {
  autoCloseExpiresAt: null | number
  autoCloseIntervalId: null | number
  autoCloseTimeoutId: null | number
  backButton: HTMLElement | null
  container: HTMLElement
  countdownIntervalId: null | number
  email: null | string
  emailError: HTMLElement | null
  emailForm: HTMLFormElement | null
  emailHoneypot: HTMLInputElement | null
  emailInput: HTMLInputElement | null
  guestCheckbox: HTMLInputElement | null
  logoutButton: HTMLElement | null
  otpCountdown: HTMLElement | null
  otpDigits: HTMLInputElement[]
  otpExpiresAt: null | number
  otpField: HTMLInputElement | null
  otpForm: HTMLFormElement | null
  otpFormError: HTMLElement | null
  otpResend: HTMLButtonElement | null
  otpResendLabel: null | string
  otpSubtitle: HTMLElement | null
  profileEmail: HTMLElement | null
  profileGuest: HTMLElement | null
  profileMember: HTMLElement | null
  profileName: HTMLElement | null
  profileNameRow: HTMLElement | null
  resendCooldownIntervalId: null | number
  resendCooldownUntil: null | number
  successCountdown: HTMLElement | null
  title: HTMLElement | null
  userType: null | UserType
  views: Map<string, HTMLElement>
}

type ViewName = 'email' | 'login-success' | 'logout-success' | 'otp' | 'profile'

class LoginFlow {
  private readonly containers: NodeListOf<HTMLElement>
  private readonly flows: FlowRefs[] = []

  constructor() {
    this.containers = getElements('login-flow')
  }

  init(): void {
    if (this.containers.length === 0) return

    this.containers.forEach((container: HTMLElement): void => {
      const refs = this.collectRefs(container)
      this.flows.push(refs)
      this.bindFlow(refs)
      const loggedIn = LoginStateService.isLoggedIn()
      this.showView(refs, loggedIn ? 'profile' : 'email')
      // The `--aside` variant renders inline with no open event, so validate and populate
      // its profile here. The modal does this on open instead (see below), so skip it to
      // avoid a redundant session call on every page load.
      if (loggedIn && refs.container.closest('dialog') === null) void this.confirmSession(refs)
    })

    document.addEventListener(LOGIN_MODAL_OPENED_EVENT, (event: Event): void => {
      const detail = (event as CustomEvent<{ startView: ViewName }>).detail
      const dialog = event.target as HTMLElement | null
      if (dialog === null) return

      const refs = this.flows.find((flow: FlowRefs): boolean => dialog.contains(flow.container))
      if (refs === undefined) return

      this.showView(refs, detail.startView)
      if (detail.startView === 'profile') void this.confirmSession(refs)
    })
  }

  private applyTitle(refs: FlowRefs): void {
    if (refs.title === null) return
    const isGuest = refs.guestCheckbox?.checked ?? false
    const next = isGuest ? refs.title.dataset.titleGuest : refs.title.dataset.titleDefault
    if (next !== undefined && next !== '') refs.title.textContent = next
  }

  private bindFlow(refs: FlowRefs): void {
    refs.emailForm?.addEventListener('submit', (event: SubmitEvent): void => {
      event.preventDefault()
      void this.handleEmailSubmit(refs)
    })

    refs.otpResend?.addEventListener('click', (): void => {
      void this.handleResend(refs)
    })

    refs.guestCheckbox?.addEventListener('change', (): void => {
      this.applyTitle(refs)
    })

    this.bindOtpDigits(refs)

    refs.otpForm?.addEventListener('submit', (event: SubmitEvent): void => {
      event.preventDefault()
      void this.handleOtpSubmit(refs)
    })

    refs.backButton?.addEventListener('click', (): void => {
      this.resetOtpForm(refs)
      this.stopOtpCountdown(refs)
      this.stopResendCooldown(refs)
      this.showView(refs, 'email')
      refs.emailInput?.focus()
    })

    refs.logoutButton?.addEventListener('click', (): void => {
      void this.handleLogout(refs)
    })
  }

  private bindOtpDigits(refs: FlowRefs): void {
    refs.otpDigits.forEach((digit: HTMLInputElement, index: number): void => {
      digit.addEventListener('input', (): void => {
        // Keep only the last digit typed (handles overwrite when input already has a value).
        const cleaned = digit.value.replace(/\D/g, '').slice(-1)
        digit.value = cleaned
        this.syncOtpAggregate(refs)

        if (cleaned !== '' && index < refs.otpDigits.length - 1) {
          refs.otpDigits[index + 1]?.focus()
        }
      })

      digit.addEventListener('keydown', (event: KeyboardEvent): void => {
        if (event.key === 'Backspace' && digit.value === '' && index > 0) {
          event.preventDefault()
          const previous = refs.otpDigits[index - 1]
          if (previous !== undefined) {
            previous.value = ''
            previous.focus()
            this.syncOtpAggregate(refs)
          }
          return
        }

        if (event.key === 'ArrowLeft' && index > 0) {
          event.preventDefault()
          refs.otpDigits[index - 1]?.focus()
          return
        }

        if (event.key === 'ArrowRight' && index < refs.otpDigits.length - 1) {
          event.preventDefault()
          refs.otpDigits[index + 1]?.focus()
        }
      })

      digit.addEventListener('focus', (): void => {
        digit.select()
      })

      digit.addEventListener('paste', (event: ClipboardEvent): void => {
        const text = event.clipboardData?.getData('text') ?? ''
        const digits = text.replace(/\D/g, '').slice(0, OTP_LENGTH - index)
        if (digits === '') return

        event.preventDefault()
        digits.split('').forEach((char: string, offset: number): void => {
          const target = refs.otpDigits[index + offset]
          if (target !== undefined) target.value = char
        })
        this.syncOtpAggregate(refs)

        const nextIndex = Math.min(index + digits.length, refs.otpDigits.length - 1)
        refs.otpDigits[nextIndex]?.focus()
      })
    })
  }

  private collectRefs(container: HTMLElement): FlowRefs {
    const views = new Map<string, HTMLElement>()
    getElementChildren(container, 'login-flow-view').forEach((view: HTMLElement): void => {
      const name = view.dataset.view ?? ''
      if (name !== '') views.set(name, view)
    })

    const emailForm = views.get('email')?.querySelector<HTMLFormElement>('form') ?? null
    const emailInput = emailForm?.querySelector<HTMLInputElement>('input[name="email"]') ?? null
    const emailHoneypot = emailForm?.querySelector<HTMLInputElement>('input[name="homepage"]') ?? null
    const guestCheckbox = emailForm?.querySelector<HTMLInputElement>('input[name="guest"]') ?? null
    const otpForm = views.get('otp')?.querySelector<HTMLFormElement>('form') ?? null
    const otpField = otpForm?.querySelector<HTMLInputElement>('input[name="otp"]') ?? null
    const otpDigits = otpForm === null
      ? []
      : Array.from(otpForm.querySelectorAll<HTMLInputElement>('[data-js="login-otp-digit"]'))

    const otpResend = getElementChild(container, 'login-flow-otp-resend') as HTMLButtonElement | null

    return {
      autoCloseExpiresAt: null,
      autoCloseIntervalId: null,
      autoCloseTimeoutId: null,
      backButton: getElementChild(container, 'login-flow-back'),
      container,
      countdownIntervalId: null,
      email: null,
      emailError: getElementChild(container, 'login-flow-email-error'),
      emailForm,
      emailHoneypot,
      emailInput,
      guestCheckbox,
      logoutButton: getElementChild(container, 'login-flow-logout'),
      otpCountdown: getElementChild(container, 'login-flow-otp-countdown'),
      otpDigits,
      otpExpiresAt: null,
      otpField,
      otpForm,
      otpFormError: getElementChild(container, 'login-flow-otp-error'),
      otpResend,
      otpResendLabel: otpResend?.textContent?.trim() ?? null,
      otpSubtitle: getElementChild(container, 'login-flow-otp-subtitle'),
      profileEmail: getElementChild(container, 'login-flow-profile-email'),
      profileGuest: getElementChild(container, 'login-flow-profile-guest'),
      profileMember: getElementChild(container, 'login-flow-profile-member'),
      profileName: getElementChild(container, 'login-flow-profile-name'),
      profileNameRow: getElementChild(container, 'login-flow-profile-name-row'),
      resendCooldownIntervalId: null,
      resendCooldownUntil: null,
      successCountdown: getElementChild(container, 'login-flow-success-countdown'),
      title: getElementChild(container, 'login-flow-title'),
      userType: null,
      views
    }
  }

  // The `vhw_login` cookie can outlive the server-side session; re-check on open so a stale
  // profile view drops back to the login step instead of appearing logged in.
  private async confirmSession(refs: FlowRefs): Promise<void> {
    try {
      const user = await AuthApiService.session()
      if (user === null) {
        LoginStateService.notifyChange(false)
        this.showView(refs, 'email')
        refs.emailInput?.focus()
        return
      }

      this.renderProfile(refs, user)
    } catch {
      // Transient middleware error — keep the optimistic UI rather than forcing a logout.
    }
  }

  private formHasErrors(form: HTMLFormElement | null): boolean {
    if (form === null) return true
    return form.querySelector('[data-js="form-ctr"].is-bad') !== null
  }

  // Honors a `redirect` query param (e.g. set by CheckoutAccessMiddleware) but only for
  // same-origin, path-only targets to prevent open redirects.
  private getRedirectTarget(): null | string {
    const raw = new URLSearchParams(window.location.search).get('redirect')
    if (raw === null || raw === '') return null

    try {
      const url = new URL(raw, window.location.origin)
      if (url.origin !== window.location.origin) return null
      return url.pathname + url.search
    } catch {
      return null
    }
  }

  private async handleEmailSubmit(refs: FlowRefs): Promise<void> {
    // Früher brach der Versuch hier wortlos ab: der Knopf sprang zurück, sonst passierte
    // nichts. Wer fälschlich als Bot eingestuft wurde, stand damit vor einer toten Schaltfläche.
    if (this.isLikelyBot(refs)) {
      this.restoreSubmitButton(refs.emailForm)
      this.showEmailError(refs, refs.container.dataset.msgGeneric ?? '')
      return
    }

    if (this.formHasErrors(refs.emailForm)) {
      this.restoreSubmitButton(refs.emailForm)
      return
    }

    const email = refs.emailInput?.value.trim() ?? ''
    if (email === '') {
      this.restoreSubmitButton(refs.emailForm)
      return
    }

    const guest = refs.guestCheckbox?.checked ?? false
    this.hideEmailError(refs)

    try {
      refs.userType = await AuthApiService.requestOtp(email, guest)
    } catch (error) {
      this.restoreSubmitButton(refs.emailForm)
      this.showErrorFeedback(refs, error, 'email')
      return
    }

    refs.email = email
    this.restoreSubmitButton(refs.emailForm)
    this.updateOtpSubtitle(refs)
    this.resetOtpForm(refs)
    this.startOtpCountdown(refs)
    this.showView(refs, 'otp')
    refs.otpDigits[0]?.focus()
  }

  private async handleLogout(refs: FlowRefs): Promise<void> {
    try {
      await AuthApiService.logout()
    } catch {
      // The server clears the cookies best-effort; reflect the logged-out UI regardless.
    }

    LoginStateService.notifyChange(false)
    this.showView(refs, 'logout-success')
  }

  private async handleOtpSubmit(refs: FlowRefs): Promise<void> {
    if (this.formHasErrors(refs.otpForm)) {
      this.restoreSubmitButton(refs.otpForm)
      return
    }

    const code = refs.otpField?.value.trim() ?? ''

    // Login context is lost after a reload; send the user back to the email step.
    if (refs.email === null || refs.userType === null) {
      this.restoreSubmitButton(refs.otpForm)
      this.resetOtpForm(refs)
      this.stopOtpCountdown(refs)
      this.showView(refs, 'email')
      refs.emailInput?.focus()
      return
    }

    let user: AuthUser
    try {
      user = await AuthApiService.verifyOtp(refs.email, code, refs.userType)
    } catch (error) {
      this.restoreSubmitButton(refs.otpForm)
      this.showErrorFeedback(refs, error, 'otp')
      return
    }

    // Seed the profile view so a later modal reopen shows the data immediately;
    // confirmSession still refreshes it against the session on the next open.
    this.renderProfile(refs, user)

    refs.otpFormError?.classList.add(IS_HIDDEN)
    this.restoreSubmitButton(refs.otpForm)
    this.stopOtpCountdown(refs)
    this.stopResendCooldown(refs)
    LoginStateService.notifyChange(true)

    const redirectTarget = this.getRedirectTarget()
    if (redirectTarget !== null) {
      window.location.assign(redirectTarget)
      return
    }

    this.showView(refs, 'login-success')
  }

  private async handleResend(refs: FlowRefs): Promise<void> {
    if (refs.email === null) return

    const guest = refs.guestCheckbox?.checked ?? false

    try {
      refs.userType = await AuthApiService.requestOtp(refs.email, guest)
    } catch (error) {
      this.showErrorFeedback(refs, error, 'otp')
      return
    }

    this.resetOtpForm(refs)
    this.startOtpCountdown(refs)
    refs.otpDigits[0]?.focus()
  }

  private hideEmailError(refs: FlowRefs): void {
    refs.emailError?.classList.add(IS_HIDDEN)
  }

  // Nur noch das Honeypot-Feld. Die frühere Mindest-Ausfüllzeit von 1,5 s traf ausgerechnet
  // Nutzer mit Browser-Autofill oder Zwischenablage: sie sind sofort fertig und galten damit als
  // Bot (VHWKUNDE-112). Gegen echte Bots war sie ohnehin wirkungslos — die warten einfach — und
  // der Missbrauchsschutz liegt serverseitig im Rate-Limiting der Middleware
  // (5 OTP-Anfragen je E-Mail und 30 je IP pro 15 Minuten, siehe config/packages/framework.yaml).
  private isLikelyBot(refs: FlowRefs): boolean {
    return (refs.emailHoneypot?.value.trim() ?? '') !== ''
  }

  private renderCountdown(refs: FlowRefs, remainingMs: number): void {
    if (refs.otpCountdown === null) return
    const template = refs.otpCountdown.dataset.template ?? '%s'
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000))
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    const formatted = `${minutes.toString()}:${seconds.toString().padStart(2, '0')}`
    refs.otpCountdown.textContent = template.replace('%s', formatted)
  }

  private renderProfile(refs: FlowRefs, user: AuthUser): void {
    const name = [user.firstName, user.lastName]
      .filter((part: null | string): part is string => part !== null && part.trim() !== '')
      .join(' ')
      .trim()
    const isGuest = user.type === 'guest'
    const showName = !isGuest && name !== ''

    refs.profileNameRow?.classList.toggle(IS_HIDDEN, !showName)
    if (refs.profileName !== null) refs.profileName.textContent = name
    refs.profileMember?.classList.toggle(IS_HIDDEN, !(showName && user.isMember))

    if (refs.profileEmail !== null) refs.profileEmail.textContent = user.email
    refs.profileGuest?.classList.toggle(IS_HIDDEN, !isGuest)
  }

  private renderResendCooldown(refs: FlowRefs, remainingMs: number): void {
    if (refs.otpResend === null) return
    const template = refs.otpResend.dataset.template ?? '%s'
    const seconds = Math.max(0, Math.ceil(remainingMs / 1000))
    refs.otpResend.textContent = template.replace('%s', seconds.toString())
  }

  private renderSuccessCountdown(refs: FlowRefs, remainingMs: number): void {
    if (refs.successCountdown === null) return
    const template = refs.successCountdown.dataset.template ?? '%s'
    const seconds = Math.max(0, Math.ceil(remainingMs / 1000))
    refs.successCountdown.textContent = template.replace('%s', seconds.toString())
  }

  private resetOtpForm(refs: FlowRefs): void {
    refs.otpDigits.forEach((digit: HTMLInputElement): void => {
      digit.value = ''
    })
    if (refs.otpField !== null) refs.otpField.value = ''
    refs.otpFormError?.classList.add(IS_HIDDEN)
    refs.otpForm?.querySelectorAll<HTMLElement>('[data-js="form-ctr"]').forEach((ctr: HTMLElement): void => {
      ctr.classList.remove('is-bad', 'is-ok')
    })
  }

  private restoreSubmitButton(form: HTMLFormElement | null): void {
    if (form === null) return
    const submitButton = form.querySelector<HTMLButtonElement>('[data-js="form-submit"]')
    if (submitButton === null) return
    submitButton.classList.remove(IS_LOADING)
    // FormValidationService disables the submit button via setTimeout(10ms).
    // Re-enable it on the next tick so we win the race.
    window.setTimeout((): void => {
      submitButton.disabled = false
    }, 20)
  }

  private showEmailError(refs: FlowRefs, message: string): void {
    if (refs.emailError === null || message === '') return
    refs.emailError.textContent = message
    refs.emailError.classList.remove(IS_HIDDEN)
  }

  /**
   * Applies everything a failed request implies: the resend lock is resynced from the server,
   * and the message is written to the error container of the step the user is on. The server
   * already picked and translated the message for the concrete reason, so it is rendered
   * as-is; the local fallback only covers a missing response body (network error, offline).
   */
  private showErrorFeedback(refs: FlowRefs, error: unknown, target: 'email' | 'otp'): void {
    if (error instanceof AuthError) {
      // "A code is already on its way" — the counting resend button conveys this better than
      // a red error line, so no message is shown at all.
      if (error.code === 'cooldown_active') {
        this.startResendCooldown(refs, error.retryAfter === null ? RESEND_COOLDOWN_MS : error.retryAfter * 1000)
        return
      }

      if (error.code === 'rate_limited' && error.retryAfter !== null) {
        this.startResendCooldown(refs, error.retryAfter * 1000)
      }

      // The pending code is gone for good — a new one can be requested straight away.
      if (CODE_GONE_CODES.has(error.code)) this.stopResendCooldown(refs)
    }

    const message = error instanceof AuthError && error.message !== ''
      ? error.message
      : refs.container.dataset.msgGeneric ?? ''

    if (target === 'email') {
      this.showEmailError(refs, message)
      return
    }

    this.showOtpError(refs, message)
  }

  private showOtpError(refs: FlowRefs, message: string): void {
    if (refs.otpFormError === null || message === '') return
    refs.otpFormError.textContent = message
    refs.otpFormError.classList.remove(IS_HIDDEN)
  }

  private showView(refs: FlowRefs, name: ViewName): void {
    this.stopSuccessAutoClose(refs)

    const targetView = name === 'login-success' || name === 'logout-success' ? 'success' : name
    const successView = refs.views.get('success')

    refs.views.forEach((view: HTMLElement, key: string): void => {
      view.classList.toggle(IS_HIDDEN, key !== targetView)
    })

    if (successView !== undefined && targetView === 'success') {
      const isLogout = name === 'logout-success'
      const title = isLogout ? successView.dataset.logoutTitle ?? '' : successView.dataset.loginTitle ?? ''
      const message = isLogout ? successView.dataset.logoutMessage ?? '' : successView.dataset.loginMessage ?? ''
      const titleElement = getElementChild(successView, 'login-flow-success-title')
      const messageElement = getElementChild(successView, 'login-flow-success-message')
      if (titleElement !== null) titleElement.textContent = title
      if (messageElement !== null) messageElement.textContent = message

      if (name === 'login-success' && refs.container.closest('dialog') !== null) {
        this.startSuccessAutoClose(refs)
      }
    }
  }

  private startOtpCountdown(refs: FlowRefs): void {
    this.stopOtpCountdown(refs)
    this.startResendCooldown(refs, RESEND_COOLDOWN_MS)
    if (refs.otpCountdown === null) return

    refs.otpExpiresAt = Date.now() + OTP_TIMEOUT_MS
    this.renderCountdown(refs, OTP_TIMEOUT_MS)
    refs.countdownIntervalId = window.setInterval((): void => {
      if (refs.otpExpiresAt === null) return
      const remaining = refs.otpExpiresAt - Date.now()
      this.renderCountdown(refs, remaining)
      if (remaining <= 0) this.stopOtpCountdown(refs)
    }, 1000)
  }

  /**
   * Turns the resend lock into something the user can see: the button disables itself and
   * counts down. Without it the click is silently swallowed and nothing appears to happen.
   */
  private startResendCooldown(refs: FlowRefs, durationMs: number): void {
    if (refs.otpResend === null) return

    this.stopResendCooldown(refs)
    refs.resendCooldownUntil = Date.now() + durationMs
    refs.otpResend.disabled = true
    this.renderResendCooldown(refs, durationMs)

    refs.resendCooldownIntervalId = window.setInterval((): void => {
      if (refs.resendCooldownUntil === null) return
      const remaining = refs.resendCooldownUntil - Date.now()
      if (remaining <= 0) {
        this.stopResendCooldown(refs)
        return
      }
      this.renderResendCooldown(refs, remaining)
    }, 1000)
  }

  private startSuccessAutoClose(refs: FlowRefs): void {
    this.stopSuccessAutoClose(refs)

    const dialog = refs.container.closest('dialog') as HTMLDialogElement | null
    if (dialog === null) return

    refs.autoCloseExpiresAt = Date.now() + SUCCESS_AUTO_CLOSE_MS
    this.renderSuccessCountdown(refs, SUCCESS_AUTO_CLOSE_MS)
    refs.successCountdown?.classList.remove(IS_HIDDEN)

    refs.autoCloseIntervalId = window.setInterval((): void => {
      if (refs.autoCloseExpiresAt === null) return
      this.renderSuccessCountdown(refs, refs.autoCloseExpiresAt - Date.now())
    }, 1000)

    refs.autoCloseTimeoutId = window.setTimeout((): void => {
      this.stopSuccessAutoClose(refs)
      dialog.close()
    }, SUCCESS_AUTO_CLOSE_MS)
  }

  private stopOtpCountdown(refs: FlowRefs): void {
    if (refs.countdownIntervalId !== null) {
      window.clearInterval(refs.countdownIntervalId)
      refs.countdownIntervalId = null
    }
    refs.otpExpiresAt = null
  }

  private stopResendCooldown(refs: FlowRefs): void {
    if (refs.resendCooldownIntervalId !== null) {
      window.clearInterval(refs.resendCooldownIntervalId)
      refs.resendCooldownIntervalId = null
    }
    refs.resendCooldownUntil = null

    if (refs.otpResend === null) return
    refs.otpResend.disabled = false
    if (refs.otpResendLabel !== null) refs.otpResend.textContent = refs.otpResendLabel
  }

  private stopSuccessAutoClose(refs: FlowRefs): void {
    if (refs.autoCloseIntervalId !== null) {
      window.clearInterval(refs.autoCloseIntervalId)
      refs.autoCloseIntervalId = null
    }
    if (refs.autoCloseTimeoutId !== null) {
      window.clearTimeout(refs.autoCloseTimeoutId)
      refs.autoCloseTimeoutId = null
    }
    refs.autoCloseExpiresAt = null
    refs.successCountdown?.classList.add(IS_HIDDEN)
  }

  private syncOtpAggregate(refs: FlowRefs): void {
    if (refs.otpField === null) return
    refs.otpField.value = refs.otpDigits.map((digit: HTMLInputElement): string => digit.value).join('')
    refs.otpFormError?.classList.add(IS_HIDDEN)
  }

  private updateOtpSubtitle(refs: FlowRefs): void {
    if (refs.otpSubtitle === null) return
    const email = refs.emailInput?.value.trim() ?? ''
    const template = refs.otpSubtitle.dataset.template ?? '%s'
    const [prefix = '', suffix = ''] = template.split('%s')
    const emailElement = document.createElement('b')
    emailElement.textContent = email
    refs.otpSubtitle.replaceChildren(
      document.createTextNode(prefix),
      emailElement,
      document.createTextNode(suffix)
    )
  }
}

const loginFlow = new LoginFlow()
loginFlow.init()
